import React, { Component } from "react";
import { Field, Formik, Form } from "formik";
import {
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Spinner,
  FormFeedback,
} from "reactstrap";
import { Redirect, Link } from "react-router-dom";
import { Avatar } from "@mui/material";
import { UserService, User } from "../generated";
import * as Yup from "yup";

export interface State {
  loading: boolean;
  success: boolean;
  error: any;
  exists: boolean;
  user: User;
  redirect: boolean;
}

const errorTemplateMessage = (field: string) =>
  `Please fill in ${field} to complete the registration.`;
const errorAlphabets = () => `Only alphabets are allowed for this field. `;

const SignInSchema = Yup.object().shape({
  firstName: Yup.string()
    .required(errorTemplateMessage("First Name"))
    .matches(/^[a-zA-Z ]*$/, errorAlphabets()),

  lastName: Yup.string()
    .required(errorTemplateMessage("Last Name"))
    .matches(/^[a-zA-Z ]*$/, errorAlphabets()),

  telephone: Yup.string()
    .required(errorTemplateMessage("Phone Number"))
    .matches(
      /^[+]?[(]?\d{3}[)]?[-\s.]?\d{3}[-\s.]?\d{4,6}$/im,
      "Phone number is not valid."
    ),

  country: Yup.string().required(errorTemplateMessage("Country")),

  town: Yup.string().required(errorTemplateMessage("Town")),
});

export class TesterOnboarding extends Component<{}, State> {
  constructor(props: {}) {
    super(props);

    this.state = {
      loading: true,
      success: null,
      error: null,
      exists: false,
      user: undefined,
      redirect: false,
    };
    this.submit = this.submit.bind(this);
  }

  async componentDidMount() {
    const r = await UserService.userGetUser();
    this.setState({
      loading: false,
    });
    if (r.user) {
      this.setState({
        success: !!r.user,
        user: r.user,
      });
    }
  }

  async submit(user: User) {
    this.setState({
      loading: true,
    });

    const r = await UserService.userCreateUser({
      user: user,
    });

    this.setState({
      loading: false,
    });
    if (r.success) {
      const r = await UserService.userGetUser();

      this.setState({
        loading: false,
      });
      if (r.user) {
        this.setState({
          success: !!r.user,
          user: r.user,
          redirect: true,
        });
      }
    } else {
      this.setState({
        error: r,
      });
    }

    return false;
  }

  render() {
    return this.state.redirect ? (
      <Redirect to={`/user`} />
    ) : (
      <div>
        <Modal
          isOpen={this.state.error != null}
          toggle={() => this.setState({ error: null })}
        >
          <ModalHeader>
            {this.state.error && this.state.error.title}
          </ModalHeader>
          <ModalBody>
            {this.state.error && JSON.stringify(this.state.error, null, 4)}
          </ModalBody>
        </Modal>
        <Formik
          enableReinitialize
          initialValues={this.state.user}
          validationSchema={SignInSchema}
          onSubmit={this.submit}
        >
          {({ errors, touched }) => (
            <Form className="">
              <Row>
                <Col lg>
                  <div className="border rounded rounded-more overflow-hidden p-5">
                    <p style={{ fontSize: "0.75em" }} className=" mb-1">
                      Profile picture
                    </p>
                    <Avatar
                      className="rounded rounded-circle"
                      style={{ width: "100px", height: "100px" }}
                      alt={
                        this.state.success
                          ? this.state.user.firstName +
                            " " +
                            this.state.user.lastName
                          : ""
                      }
                      src={this.state.success ? this.state.user.imageUrl : ""}
                    />

                    <FormGroup>
                      <Label
                        htmlFor={`firstName`}
                        style={{ fontSize: "0.75em" }}
                      >
                        First name
                      </Label>
                      <Input
                        className="bg-light border-0"
                        name="firstName"
                        placeholder="First name"
                        tag={Field}
                        valid={!errors.firstName && touched.firstName}
                        invalid={errors.firstName && touched.firstName}
                      />
                      {errors.firstName && touched.firstName && (
                        <FormFeedback>{errors.firstName}</FormFeedback>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <Label
                        htmlFor={`lastName`}
                        style={{ fontSize: "0.75em" }}
                      >
                        Last name
                      </Label>
                      <Input
                        className="bg-light border-0"
                        name="lastName"
                        placeholder="Last Name"
                        tag={Field}
                        valid={!errors.lastName && touched.lastName}
                        invalid={errors.lastName && touched.lastName}
                      />
                      {errors.lastName && touched.lastName && (
                        <FormFeedback>{errors.lastName}</FormFeedback>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <Label
                        htmlFor={`telephone`}
                        style={{ fontSize: "0.75em" }}
                      >
                        Phone Number
                      </Label>
                      <Input
                        className="bg-light border-0"
                        name="telephone"
                        type="text"
                        placeholder="Phone Number"
                        tag={Field}
                        valid={!errors.telephone && touched.telephone}
                        invalid={errors.telephone && touched.telephone}
                      />
                      {errors.telephone && touched.telephone && (
                        <FormFeedback>{errors.telephone}</FormFeedback>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor={`country`} style={{ fontSize: "0.75em" }}>
                        Country
                      </Label>
                      <Input
                        className="bg-light border-0"
                        name="country"
                        type="text"
                        placeholder="Country"
                        tag={Field}
                        valid={!errors.country && touched.country}
                        invalid={errors.country && touched.country}
                      />
                      {errors.country && touched.country && (
                        <FormFeedback>{errors.country}</FormFeedback>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <Label htmlFor={`town`} style={{ fontSize: "0.75em" }}>
                        Town
                      </Label>
                      <Input
                        className="bg-light border-0"
                        name="town"
                        type="text"
                        placeholder="Town"
                        tag={Field}
                        valid={!errors.town && touched.town}
                        invalid={errors.town && touched.town}
                      />
                      {errors.town && touched.town && (
                        <FormFeedback>{errors.town}</FormFeedback>
                      )}
                    </FormGroup>

                    <FormGroup>
                      <Label style={{ fontSize: "0.75em" }}>Notes</Label>
                      <Input
                        className="bg-light border-0"
                        type="textarea"
                        name="notes"
                        placeholder="Notes"
                        defaultValue={
                          this.state.success ? this.state.user.notes : ""
                        }
                      />
                    </FormGroup>
                    {this.state.loading && <Spinner />}
                  </div>
                </Col>
                <Col lg className="mt-4 mt-lg-0">
                  <h3 className="mb-3">Update your profile</h3>
                  <p className="mb-3">
                    Keep your profile updated so your information is always up
                    to date, and we are able to get in touch when needed.
                  </p>
                  <Button className="px-4 mr-2" color="primary">
                    Complete sign-up
                  </Button>{" "}
                  <Link
                    className="btn border text-dark px-4 border-primary"
                    to="/user"
                  >
                    Cancel
                  </Link>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}
